/**
 * 「提醒事項」分頁內容(REQ-20260918002)。
 *
 * 記的是「這次先這樣、下次要改正」的交代 —— 印刷顏色要調、紙張要加厚、
 * 這次的特價下次下單要恢復原價。未完成的排最上面,做完按一下收進「已完成」。
 *
 * 與訪談列上那顆「🔔 提醒中」鈴鐺**不是同一件事**:鈴鐺是每天推播催你去聯絡對方,
 * 這裡是「下次做這家生意時要記得的規則」,兩者互不影響。
 *
 * REQ-20260922001 起輸入拆成四格:記事日期 / 記事商品 / 記要要點 / 記事內容。
 * 只有「記事內容」必填(它就是原本那一格,欄位仍是 `content`);
 * 另外三格舊資料一律是 null,沒填的段落整段不顯示 —— 畫面與改動前逐字相同。
 */
import { useState } from 'react';
import type { ReminderResponse } from '@app/shared';
import {
  REMINDER_CONTENT_MAX,
  REMINDER_KEY_POINT_MAX,
  REMINDER_PRODUCT_MAX,
} from '@app/shared';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { getApiErrorMessage } from '../../lib/api-client';
import { useAuthStore } from '../auth/store';
import {
  useCreateReminder,
  useDeleteReminder,
  useReminders,
  useToggleReminderDone,
  useUpdateReminder,
  type ReminderOwnerType,
} from './api';

interface Props {
  ownerType: ReminderOwnerType;
  ownerId: number;
  /** 客戶才有:owner 為自己的 SALES 才能寫。廠商沒有這個概念,不用傳。 */
  customerOwnerId?: number;
}

/**
 * 「YYYY-MM-DD」。**明確指定 Asia/Taipei** —— 直接 `slice(0, 10)` 取到的是 UTC 日期,
 * 台灣時間 00:00-08:00 建立的提醒會顯示成前一天(CLAUDE.md §6.1)。
 */
function formatTaipeiDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** 記事日期的預設值 —— 今天(台北)。理由同上:不用 `toISOString().slice(0, 10)`。 */
function todayTaipei(): string {
  return formatTaipeiDate(new Date().toISOString());
}

/** 四格輸入的草稿。日期預設今天,其餘三格空白。 */
interface Draft {
  noteDate: string;
  productName: string;
  keyPoint: string;
  content: string;
}

function emptyDraft(): Draft {
  return { noteDate: todayTaipei(), productName: '', keyPoint: '', content: '' };
}

/** 空字串 → null:`''` 與「沒填」是同一件事,不要送空字串進 DB。 */
function orNull(v: string): string | null {
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
}

const fieldCls = 'border rounded px-3 py-2 text-sm min-w-0';

/**
 * 一筆提醒的分類內容(要點 / 日期 · 商品 / 內容)。
 * 三個新欄位**沒填就整段不顯示** —— 舊資料(三欄皆 null)看起來與改動前逐字相同,
 * 不會冒出空的標題行或孤零零的「·」。
 */
function ReminderBody({
  row,
  done,
}: {
  row: ReminderResponse;
  done: boolean;
}): JSX.Element {
  const meta = [row.noteDate, row.productName].filter((v): v is string => !!v);
  return (
    <>
      {row.keyPoint ? (
        <p
          className={`font-semibold break-words ${done ? 'text-slate-500 line-through' : 'text-amber-900'}`}
          data-testid={`reminder-keypoint-${row.id}`}
        >
          {row.keyPoint}
        </p>
      ) : null}
      {meta.length > 0 ? (
        <p
          className={`text-xs ${done ? 'text-slate-400' : 'text-amber-700'}`}
          data-testid={`reminder-meta-${row.id}`}
        >
          {meta.join(' · ')}
        </p>
      ) : null}
      <p
        className={`whitespace-pre-wrap break-words ${done ? 'text-slate-500 line-through' : 'text-amber-900'}`}
      >
        {row.content}
      </p>
    </>
  );
}

export function ReminderPanel({ ownerType, ownerId, customerOwnerId }: Props): JSX.Element {
  const me = useAuthStore((s) => s.user);
  // 權限規則沿用 InterviewTimeline:
  // 客戶 → ADMIN 或 SALES 且擁有此客戶;廠商 → ADMIN 或 SALES(無 owner 概念)
  const canWrite =
    me?.role === 'ADMIN' ||
    (me?.role === 'SALES' && (ownerType === 'supplier' || customerOwnerId === me.id));

  const { data, isLoading, isError, error } = useReminders(ownerType, ownerId, 'all');
  const createMutation = useCreateReminder(ownerType, ownerId);
  const updateMutation = useUpdateReminder(ownerType, ownerId);
  const toggleMutation = useToggleReminderDone(ownerType, ownerId);
  const deleteMutation = useDeleteReminder(ownerType, ownerId);

  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editing, setEditing] = useState<({ id: number } & Draft) | null>(null);
  const [doneOpen, setDoneOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ReminderResponse | null>(null);

  const rows = data ?? [];
  const openRows = rows.filter((r) => r.doneAt == null);
  const doneRows = rows.filter((r) => r.doneAt != null);

  const submitDraft = (): void => {
    const content = draft.content.trim();
    if (!content) return;
    createMutation.mutate(
      {
        content,
        noteDate: orNull(draft.noteDate),
        productName: orNull(draft.productName),
        keyPoint: orNull(draft.keyPoint),
      },
      // 送出後四格一起清空,日期回復成今天
      { onSuccess: () => setDraft(emptyDraft()) },
    );
  };

  const submitEdit = (): void => {
    if (!editing) return;
    const content = editing.content.trim();
    if (!content) return;
    updateMutation.mutate(
      {
        id: editing.id,
        input: {
          content,
          noteDate: orNull(editing.noteDate),
          productName: orNull(editing.productName),
          keyPoint: orNull(editing.keyPoint),
        },
      },
      { onSuccess: () => setEditing(null) },
    );
  };

  /** Enter 送出:這些格子不在 <form> 裡,Enter 不會自己送出,要手動接。 */
  const enterToSubmit =
    (submit: () => void) =>
    (e: React.KeyboardEvent): void => {
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    };

  return (
    <section data-testid="reminder-panel">
      <header className="mb-3 flex items-center justify-between min-h-[2.25rem]">
        <h2 className="text-lg font-semibold" data-testid="reminder-open-count">
          下次注意事項（{isLoading ? '…' : `未完成 ${openRows.length}`}）
        </h2>
      </header>

      {canWrite ? (
        // 四格 + 新增鈕。窄螢幕一格一行,寬螢幕「日期 / 商品 / 要點」擠一列、
        // 「內容 + 新增」一列 —— 內容是必填也最長,給它整列。
        <div className="mb-3 grid gap-2 sm:grid-cols-[10rem_1fr_1fr]">
          <input
            type="date"
            value={draft.noteDate}
            onChange={(e) => setDraft({ ...draft, noteDate: e.target.value })}
            onKeyDown={enterToSubmit(submitDraft)}
            aria-label="記事日期"
            className={fieldCls}
            data-testid="reminder-date-input"
          />
          <input
            type="text"
            value={draft.productName}
            maxLength={REMINDER_PRODUCT_MAX}
            onChange={(e) => setDraft({ ...draft, productName: e.target.value })}
            onKeyDown={enterToSubmit(submitDraft)}
            placeholder="記事商品(可留空)"
            aria-label="記事商品"
            className={fieldCls}
            data-testid="reminder-product-input"
          />
          <input
            type="text"
            value={draft.keyPoint}
            maxLength={REMINDER_KEY_POINT_MAX}
            onChange={(e) => setDraft({ ...draft, keyPoint: e.target.value })}
            onKeyDown={enterToSubmit(submitDraft)}
            placeholder="記要要點(可留空)"
            aria-label="記要要點"
            className={fieldCls}
            data-testid="reminder-keypoint-input"
          />
          <div className="flex gap-2 sm:col-span-3">
            <input
              type="text"
              value={draft.content}
              maxLength={REMINDER_CONTENT_MAX}
              onChange={(e) => setDraft({ ...draft, content: e.target.value })}
              onKeyDown={enterToSubmit(submitDraft)}
              placeholder="記事內容,例:下次下單印刷顏色要改深一階"
              aria-label="記事內容"
              className={`${fieldCls} flex-1`}
              data-testid="reminder-input"
            />
            <button
              type="button"
              onClick={submitDraft}
              disabled={draft.content.trim().length === 0 || createMutation.isPending}
              className="bg-blue-600 text-white rounded px-3 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50 shrink-0"
              data-testid="reminder-add-btn"
            >
              {createMutation.isPending ? '新增中…' : '新增'}
            </button>
          </div>
        </div>
      ) : null}

      {createMutation.isError ? (
        <p role="alert" className="mb-2 text-sm text-red-600">
          {getApiErrorMessage(createMutation.error, '新增失敗')}
        </p>
      ) : null}

      {isLoading ? (
        <p role="status" className="text-slate-500">
          提醒事項載入中…
        </p>
      ) : isError ? (
        <p role="alert" className="text-red-600">
          提醒事項載入失敗：{getApiErrorMessage(error, '請稍後再試')}
        </p>
      ) : openRows.length === 0 ? (
        <p className="text-slate-500 text-sm" data-testid="reminder-empty">
          目前沒有未完成的注意事項。
          {canWrite ? '在上面輸入「下次要改正什麼」即可新增。' : ''}
        </p>
      ) : (
        <ol className="grid gap-2" data-testid="reminder-open-list">
          {openRows.map((r) => (
            <li
              key={r.id}
              className="border border-amber-300 bg-amber-50 rounded p-3 text-sm"
              data-testid={`reminder-item-${r.id}`}
            >
              {editing?.id === r.id ? (
                <div
                  className="grid gap-2 sm:grid-cols-[10rem_1fr_1fr]"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setEditing(null);
                  }}
                >
                  <input
                    type="date"
                    value={editing.noteDate}
                    onChange={(e) => setEditing({ ...editing, noteDate: e.target.value })}
                    onKeyDown={enterToSubmit(submitEdit)}
                    aria-label="編輯記事日期"
                    className={fieldCls}
                    data-testid={`reminder-edit-date-${r.id}`}
                  />
                  <input
                    type="text"
                    value={editing.productName}
                    maxLength={REMINDER_PRODUCT_MAX}
                    onChange={(e) => setEditing({ ...editing, productName: e.target.value })}
                    onKeyDown={enterToSubmit(submitEdit)}
                    placeholder="記事商品(可留空)"
                    aria-label="編輯記事商品"
                    className={fieldCls}
                    data-testid={`reminder-edit-product-${r.id}`}
                  />
                  <input
                    type="text"
                    value={editing.keyPoint}
                    maxLength={REMINDER_KEY_POINT_MAX}
                    onChange={(e) => setEditing({ ...editing, keyPoint: e.target.value })}
                    onKeyDown={enterToSubmit(submitEdit)}
                    placeholder="記要要點(可留空)"
                    aria-label="編輯記要要點"
                    className={fieldCls}
                    data-testid={`reminder-edit-keypoint-${r.id}`}
                  />
                  <div className="flex gap-2 sm:col-span-3">
                    <input
                      type="text"
                      value={editing.content}
                      maxLength={REMINDER_CONTENT_MAX}
                      autoFocus
                      onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                      onKeyDown={enterToSubmit(submitEdit)}
                      aria-label="編輯記事內容"
                      className={`${fieldCls} flex-1`}
                      data-testid={`reminder-edit-input-${r.id}`}
                    />
                    <button
                      type="button"
                      onClick={submitEdit}
                      disabled={editing.content.trim().length === 0 || updateMutation.isPending}
                      className="border border-blue-600 text-blue-700 rounded px-2 py-1 hover:bg-blue-50 disabled:opacity-50 shrink-0"
                      data-testid={`reminder-edit-save-${r.id}`}
                    >
                      儲存
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(null)}
                      className="border rounded px-2 py-1 hover:bg-slate-100 shrink-0"
                      data-testid={`reminder-edit-cancel-${r.id}`}
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <ReminderBody row={r} done={false} />
                    <p className="mt-1 text-xs text-amber-700">
                      {r.createdBy.displayName} · {formatTaipeiDate(r.createdAt)}
                    </p>
                  </div>
                  {canWrite ? (
                    <div className="flex gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() =>
                          setEditing({
                            id: r.id,
                            // 沒填過的三格開成空字串;日期不預設今天,
                            // 免得編輯內容時順手把一個沒人填過的日期寫進去
                            noteDate: r.noteDate ?? '',
                            productName: r.productName ?? '',
                            keyPoint: r.keyPoint ?? '',
                            content: r.content,
                          })
                        }
                        className="border rounded px-2 py-0.5 text-xs hover:bg-amber-100"
                        data-testid={`reminder-edit-btn-${r.id}`}
                      >
                        編輯
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleMutation.mutate({ id: r.id, done: true })}
                        disabled={toggleMutation.isPending}
                        className="border border-emerald-600 text-emerald-700 rounded px-2 py-0.5 text-xs hover:bg-emerald-50 disabled:opacity-50"
                        data-testid={`reminder-done-btn-${r.id}`}
                      >
                        完成
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingDelete(r)}
                        className="border border-red-600 text-red-600 rounded px-2 py-0.5 text-xs hover:bg-red-50"
                        data-testid={`reminder-delete-btn-${r.id}`}
                      >
                        刪除
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {doneRows.length > 0 ? (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setDoneOpen((v) => !v)}
            aria-expanded={doneOpen}
            className="text-sm text-slate-600 hover:text-slate-900"
            data-testid="reminder-done-toggle"
          >
            {doneOpen ? '▾' : '▸'} 已完成 ({doneRows.length})
          </button>
          {doneOpen ? (
            <ol className="mt-2 grid gap-2" data-testid="reminder-done-list">
              {doneRows.map((r) => (
                <li
                  key={r.id}
                  className="border rounded p-3 text-sm bg-white"
                  data-testid={`reminder-item-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <ReminderBody row={r} done />
                      <p className="mt-1 text-xs text-slate-400">
                        {r.doneBy ? `${r.doneBy.displayName} 於 ` : ''}
                        {r.doneAt ? formatTaipeiDate(r.doneAt) : ''} 完成
                      </p>
                    </div>
                    {canWrite ? (
                      <button
                        type="button"
                        onClick={() => toggleMutation.mutate({ id: r.id, done: false })}
                        disabled={toggleMutation.isPending}
                        className="border rounded px-2 py-0.5 text-xs hover:bg-slate-100 disabled:opacity-50 shrink-0"
                        data-testid={`reminder-undone-btn-${r.id}`}
                      >
                        復原
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          ) : null}
        </div>
      ) : null}

      <ConfirmDialog
        open={pendingDelete != null}
        title="刪除這則提醒事項?"
        message={
          <>
            將刪除「<strong>{pendingDelete?.keyPoint || pendingDelete?.content}</strong>」。
            做完了想留紀錄的話請改按「完成」—— 刪除之後畫面上就找不到了。
          </>
        }
        confirmLabel="刪除"
        cancelLabel="取消"
        tone="danger"
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </section>
  );
}
